/**
 * Prioritization Task Types
 * 
 * A PrioritizationTask represents a discrete unit of prioritization work:
 * - A specific tool (e.g., stack ranking, dot voting)
 * - A set of items to prioritize
 * - A time window for participation
 * - A set of participants
 * - Collected responses and aggregated results
 */

// ============================================================================
// Task Status
// ============================================================================

/**
 * Task lifecycle states
 * 
 * DRAFT - Task created but not yet open for responses
 * OPEN - Task is accepting responses from participants
 * CLOSED - Time window has ended, no more responses accepted
 * COMPLETED - Results have been reviewed and finalized
 */
export type TaskStatus = 'draft' | 'open' | 'closed' | 'completed';

/**
 * Task timing mode
 * 
 * ASYNC - Participants complete at their own pace within a time window
 * LIVE - Participants complete together in a scheduled session
 */
export type TaskMode = 'async' | 'live';

// ============================================================================
// Task Category
// ============================================================================

/**
 * Task category determines what type of work the participant is doing.
 * This unifies prioritization tasks with prerequisite tasks into one model.
 * 
 * PRIORITIZATION - Ranking, voting, scoring items
 * VALUE_ENTRY - Provide attribute values for items (e.g., effort estimates)
 * CRITERIA_SELECTION - Select which attributes to use as criteria
 * WEIGHT_ASSIGNMENT - Assign weights to criteria
 * ITEM_REVIEW - Review/approve items for inclusion
 */
export type TaskCategory = 
  | 'prioritization'      // Ranking, voting, scoring
  | 'value_entry'         // Provide attribute values
  | 'criteria_selection'  // Select criteria
  | 'weight_assignment'   // Assign weights
  | 'item_review'         // Review/approve items
  | 'catchball_proposal'; // Form and return a Catchball proposal

/**
 * How to express a value for value_entry tasks
 */
export type ValueFormat = 
  | 'number'              // Plain number (e.g., 1-100)
  | 'fibonacci'           // Fibonacci sequence (1, 2, 3, 5, 8, 13, 21, ...)
  | 'tshirt'              // T-shirt sizing (XS, S, M, L, XL)
  | 'scale_1_5'           // 1-5 scale
  | 'scale_1_10'          // 1-10 scale
  | 'custom';             // Custom options

/**
 * Configuration for value_entry tasks
 */
export interface ValueEntryConfig {
  /** How to express the value */
  format: ValueFormat;
  
  /** Minimum value (for number/scale formats) */
  min?: number;
  
  /** Maximum value (for number/scale formats) */
  max?: number;
  
  /** Custom options (for custom format) */
  options?: Array<{ value: string | number; label: string }>;
  
  /** Whether to allow notes per item */
  allowNotes?: boolean;
}

/**
 * Task category labels for display
 */
export const TASK_CATEGORY_LABELS: Record<TaskCategory, string> = {
  prioritization: 'Prioritization',
  value_entry: 'Value Entry',
  criteria_selection: 'Criteria Selection',
  weight_assignment: 'Weight Assignment',
  item_review: 'Item Review',
  catchball_proposal: 'Catchball Proposal',
};

/**
 * Task category icons
 */
export const TASK_CATEGORY_ICONS: Record<TaskCategory, string> = {
  prioritization: 'ListOrdered',
  value_entry: 'FileEdit',
  criteria_selection: 'ListChecks',
  weight_assignment: 'Scale',
  item_review: 'CheckCircle',
  catchball_proposal: 'Send',
};

// ============================================================================
// Tool Configuration
// ============================================================================

/**
 * Available prioritization tools that can be used in tasks
 */
export type PrioritizationToolId = 
  | 'stack-ranking'
  | 'dot-voting'
  | 'pairwise-comparison'
  | 'rice-scoring'
  | 'moscow-method'
  | 'wsjf'
  | 'impact-effort'
  | 'buy-a-feature'
  | 'eisenhower-matrix'
  | 'ahp';

/**
 * Tool metadata for display
 */
export interface ToolInfo {
  id: PrioritizationToolId;
  name: string;
  description: string;
  icon: string;
  supportsAsync: boolean;
  supportsLive: boolean;
  requiresConfig?: boolean;
  configSchema?: Record<string, unknown>;
}

/**
 * Available tools registry
 */
export const PRIORITIZATION_TOOLS: ToolInfo[] = [
  {
    id: 'stack-ranking',
    name: 'Stack Ranking',
    description: 'Drag and drop items to create a single ordered list from most to least important.',
    icon: 'ListOrdered',
    supportsAsync: true,
    supportsLive: true,
  },
  {
    id: 'dot-voting',
    name: 'Dot Voting',
    description: 'Allocate a limited number of dots to items you consider most important.',
    icon: 'Vote',
    supportsAsync: true,
    supportsLive: true,
    requiresConfig: true,
    configSchema: { dotsPerParticipant: 'number' },
  },
  {
    id: 'pairwise-comparison',
    name: 'Pairwise Comparison',
    description: 'Compare items two at a time to determine relative priority.',
    icon: 'ArrowUpDown',
    supportsAsync: true,
    supportsLive: true,
  },
  {
    id: 'rice-scoring',
    name: 'RICE Scoring',
    description: 'Score items on Reach, Impact, Confidence, and Effort.',
    icon: 'Calculator',
    supportsAsync: true,
    supportsLive: true,
  },
  {
    id: 'moscow-method',
    name: 'MoSCoW Method',
    description: 'Categorize items as Must have, Should have, Could have, or Won\'t have.',
    icon: 'Boxes',
    supportsAsync: true,
    supportsLive: true,
  },
  {
    id: 'wsjf',
    name: 'WSJF',
    description: 'Weighted Shortest Job First - prioritize based on cost of delay divided by job size.',
    icon: 'Timer',
    supportsAsync: true,
    supportsLive: true,
  },
  {
    id: 'impact-effort',
    name: 'Impact/Effort Matrix',
    description: 'Plot items on a 2x2 matrix of impact vs. effort.',
    icon: 'Scale',
    supportsAsync: true,
    supportsLive: true,
  },
  {
    id: 'buy-a-feature',
    name: 'Buy a Feature',
    description: 'Give participants a budget to "purchase" their preferred features.',
    icon: 'DollarSign',
    supportsAsync: true,
    supportsLive: true,
    requiresConfig: true,
    configSchema: { budgetPerParticipant: 'number' },
  },
  {
    id: 'eisenhower-matrix',
    name: 'Eisenhower Matrix',
    description: 'Categorize items by urgency and importance.',
    icon: 'LayoutGrid',
    supportsAsync: true,
    supportsLive: true,
  },
  {
    id: 'ahp',
    name: 'AHP',
    description: 'Analytic Hierarchy Process - structured pairwise comparison with criteria weighting.',
    icon: 'Target',
    supportsAsync: true,
    supportsLive: true,
  },
];

// ============================================================================
// Task Response
// ============================================================================

/**
 * A single participant's response to a task
 */
export interface TaskResponse {
  id: string;
  taskId: string;
  participantId: string;
  participantName: string;
  
  /** When the response was submitted */
  submittedAt: Date;
  
  /** When the response was last updated (if changes allowed) */
  updatedAt?: Date;
  
  /** Tool-specific response data */
  data: TaskResponseData;
  
  /** Optional notes from the participant */
  notes?: string;
}

/**
 * Tool-specific response data types
 */
export type TaskResponseData = 
  | StackRankingResponse
  | DotVotingResponse
  | PairwiseComparisonResponse
  | ScoringResponse
  | CategoryResponse
  | MatrixResponse
  | ValueEntryResponse
  | CriteriaSelectionResponse
  | WeightAssignmentResponse
  | ItemReviewResponse;

export interface StackRankingResponse {
  type: 'stack-ranking';
  /** Ordered list of item IDs from highest to lowest priority */
  ranking: string[];
}

export interface DotVotingResponse {
  type: 'dot-voting';
  /** Map of item ID to number of dots allocated */
  votes: Record<string, number>;
}

export interface PairwiseComparisonResponse {
  type: 'pairwise-comparison';
  /** Map of "itemA:itemB" to winner item ID */
  comparisons: Record<string, string>;
}

export interface ScoringResponse {
  type: 'rice-scoring' | 'wsjf';
  /** Map of item ID to score components */
  scores: Record<string, Record<string, number>>;
}

export interface CategoryResponse {
  type: 'moscow-method';
  /** Map of item ID to category */
  categories: Record<string, string>;
}

export interface MatrixResponse {
  type: 'impact-effort' | 'eisenhower-matrix';
  /** Map of item ID to {x, y} position */
  positions: Record<string, { x: number; y: number }>;
}

// ============================================================================
// Non-Prioritization Task Response Types
// ============================================================================

/**
 * Response for value_entry tasks
 */
export interface ValueEntryResponse {
  type: 'value-entry';
  /** Map of item ID to value */
  values: Record<string, string | number>;
  /** Optional notes per item */
  notes?: Record<string, string>;
}

/**
 * Response for criteria_selection tasks
 */
export interface CriteriaSelectionResponse {
  type: 'criteria-selection';
  /** Selected attribute IDs to use as criteria */
  selectedCriteriaIds: string[];
}

/**
 * Response for weight_assignment tasks
 */
export interface WeightAssignmentResponse {
  type: 'weight-assignment';
  /** Map of criterion ID to weight (should sum to 100) */
  weights: Record<string, number>;
  /** Method used to derive weights */
  method: 'equal' | 'manual' | 'pairwise';
}

/**
 * Response for item_review tasks
 */
export interface ItemReviewResponse {
  type: 'item-review';
  /** Map of item ID to approval status */
  decisions: Record<string, 'approved' | 'rejected' | 'needs_revision'>;
  /** Notes per item */
  notes?: Record<string, string>;
}

// ============================================================================
// Aggregated Results
// ============================================================================

/**
 * Aggregated results from all participant responses
 */
export interface AggregatedResult {
  taskId: string;
  computedAt: Date;
  
  /** Number of responses included in aggregation */
  responseCount: number;
  
  /** Tool-specific aggregated data */
  data: AggregatedResultData;
  
  /** Final ranked list of items (if applicable) */
  finalRanking?: string[];
  
  /** Confidence/agreement metrics */
  agreementScore?: number;
}

export type AggregatedResultData = 
  | AggregatedRanking
  | AggregatedVotes
  | AggregatedScores
  | AggregatedCategories;

export interface AggregatedRanking {
  type: 'ranking';
  /** Average rank per item */
  averageRanks: Record<string, number>;
  /** Standard deviation of ranks */
  rankVariance: Record<string, number>;
}

export interface AggregatedVotes {
  type: 'votes';
  /** Total votes per item */
  totalVotes: Record<string, number>;
  /** Percentage of possible votes */
  votePercentage: Record<string, number>;
}

export interface AggregatedScores {
  type: 'scores';
  /** Average score per item */
  averageScores: Record<string, number>;
  /** Score breakdown by component */
  componentScores: Record<string, Record<string, number>>;
}

export interface AggregatedCategories {
  type: 'categories';
  /** Majority category per item */
  majorityCategory: Record<string, string>;
  /** Category distribution per item */
  categoryDistribution: Record<string, Record<string, number>>;
}

// ============================================================================
// Main Task Entity
// ============================================================================

/**
 * Tool-specific configuration
 */
export interface TaskToolConfig {
  /** For dot voting: dots per participant */
  dotsPerParticipant?: number;
  
  /** For buy a feature: budget per participant */
  budgetPerParticipant?: number;
  
  /** For scoring tools: which components to use */
  scoreComponents?: string[];
  
  /** For matrix tools: axis labels */
  axisLabels?: { x: string; y: string };
  
  /** Custom configuration */
  custom?: Record<string, unknown>;

  /** An ask (migration 1054): one person asked for an estimate (lib/askForEstimate.ts). */
  ask?: boolean;
  /** The attribute an ask or an agent's question is about. */
  attribute_key?: string;
  /** The scale the value is entered on. */
  scale_id?: string | null;
}

/**
 * A discrete prioritization task within a cycle.
 * 
 * CANONICAL MODEL (Doc 02, Doc 04):
 * - A Task represents a responsibility or obligation assigned to a user
 * - A Task does NOT contain work and does NOT own execution state
 * - A Task MUST reference exactly one Session (sessionId is required)
 * - A Task is complete when its referenced Session completes
 * 
 * INVARIANT: A Task NEVER contains work. A Task ALWAYS points to where work happens.
 * 
 * Some fields below are marked @deprecated - they belong on Session, not Task.
 * They remain for backward compatibility but new code should not use them.
 */
export interface PrioritizationTask {
  id: string;
  
  /** Parent cycle ID */
  cycleId: string;
  
  /** Human-readable name */
  name: string;
  
  /** Optional description */
  description?: string;
  
  // ========================================
  // Task Category
  // ========================================
  
  /** 
   * Task category - determines what type of work this is.
   * Defaults to 'prioritization' for backward compatibility.
   */
  category: TaskCategory;
  
  /** For value_entry tasks: which attribute to provide values for */
  attributeId?: string;
  
  /** For value_entry tasks: attribute name for display */
  attributeName?: string;
  
  /** For value_entry tasks: configuration for how to express values */
  valueEntryConfig?: ValueEntryConfig;
  
  // ========================================
  // What to prioritize (for prioritization category)
  // ========================================
  
  /** Which prioritization tool to use (required for category='prioritization') */
  toolId: PrioritizationToolId;
  
  /** Tool-specific configuration */
  toolConfig?: TaskToolConfig;
  
  /** Items to be prioritized or reviewed */
  itemIds: string[];
  
  // ========================================
  // When
  // ========================================
  
  /** Task status */
  status: TaskStatus;
  
  /** Timing mode */
  mode: TaskMode;
  
  /** When the task opens for responses (async mode) */
  opensAt?: Date;
  
  /** When the task closes for responses (async mode) */
  closesAt?: Date;
  
  /** Scheduled session time (live mode) */
  scheduledAt?: Date;
  
  /** Expected duration in minutes (live mode) */
  duration?: number;
  
  // ========================================
  // Who
  // ========================================
  
  /** Task facilitator/owner */
  facilitatorId: string;
  
  /** Participants who should complete the task */
  participantIds: string[];
  
  /** Whether participants can see each other's responses */
  allowViewOthers: boolean;
  
  /** Whether participants can change their response after submission */
  allowChanges: boolean;
  
  // ========================================
  // Responses & Results
  // @deprecated - These belong on Session, not Task (per canonical model)
  // Kept for backward compatibility; new code should use Session.responses
  // ========================================
  
  /** 
   * Collected responses from participants 
   * @deprecated Use Session.responses instead - Tasks don't own execution state
   */
  responses: TaskResponse[];
  
  /** 
   * Aggregated result (computed when task is closed) 
   * @deprecated Use Session.aggregatedResult instead - Tasks don't own execution state
   */
  aggregatedResult?: AggregatedResult;
  
  // ========================================
  // Session Link (REQUIRED per Canonical Model)
  // ========================================
  
  /**
   * Link to the PrioritizationSession where work happens.
   * 
   * CANONICAL MODEL (Doc 02, Doc 04):
   * - A Task represents a responsibility assignment, not an execution container
   * - A Task MUST reference exactly one Session
   * - A Task is complete when its referenced Session completes
   * - Tasks MUST NOT exist without a valid session_id
   * 
   * This field is REQUIRED. Tasks without sessions are invalid.
   */
  sessionId: string;
  
  /**
   * Human-readable intent/purpose of this task.
   * Answers: "What am I expected to do?"
   * Example: "Rank features for Q2 release", "Provide effort estimates"
   */
  intent?: string;
  
  // ========================================
  // Metadata
  // ========================================
  
  createdAt: Date;
  createdBy: string;
  updatedAt: Date;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get completion stats for a task
 */
export function getTaskCompletionStats(task: PrioritizationTask): {
  total: number;
  completed: number;
  percentage: number;
} {
  const total = task.participantIds.length;
  const completed = task.responses.length;
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  return { total, completed, percentage };
}

/**
 * Check if a task is currently accepting responses
 */
export function isTaskOpen(task: PrioritizationTask): boolean {
  if (task.status !== 'open') return false;
  
  if (task.mode === 'async' && task.closesAt) {
    return new Date() < new Date(task.closesAt);
  }
  
  return true;
}

/**
 * Check if a participant has completed a task
 */
export function hasParticipantCompleted(task: PrioritizationTask, participantId: string): boolean {
  return task.responses.some(r => r.participantId === participantId);
}

/**
 * Get tool info by ID
 */
export function getToolInfo(toolId: PrioritizationToolId): ToolInfo | undefined {
  return PRIORITIZATION_TOOLS.find(t => t.id === toolId);
}
